/**
 * Register all route → layout slot mappings here.
 * Imported once at app boot (side-effect import).
 *
 * Adding a new page-level layout = add an entry below.
 * No need to touch ResponsiveLayout or Shell.
 */

import { ConnectorPageBreadcrumb } from "../connectors";
import {
  ConnectorInstanceSidebar,
  KgSecondarySidebarConnected,
} from "../organisms";
import { DataStudioSecondaryPanelConnected } from "../../pages/DataStudio";
import { STORY_DETAIL_PATH } from "../../hooks/useGlobalPath";
import { registerLayout } from "./layoutRegistry";

/** Pattern that matches `/data-connectors/:connectionId/tables/:schema/:tableName`. */
const CONNECTOR_DATASET_TABLE_PATH =
  /^\/data-connectors\/[^/]+\/tables\/[^/]+\/[^/]+$/;

/** Pattern that matches `/data-connectors/:connectionId` (and child routes). */
const CONNECTOR_DETAIL_PREFIX = /^\/data-connectors\/[^/]+/;

/** Pattern that matches `/data-connectors/:connectionId/data-studio`. */
const CONNECTOR_DATA_STUDIO_PATH =
  /^\/data-connectors\/[^/]+\/data-studio(?:\/|$)/;

/** Data Studio root and future child routes, without matching `/data-studios`. */
const DATA_STUDIO_PREFIX = /^\/data-studio(?:\/|$)/;

/** Agents catalog and future child routes, without matching `/agent-settings`. */
const AGENTS_PREFIX = /^\/agents(?:\/|$)/;

/** The Home page itself (not `/home-old`). */
const HOME_PATH = /^\/home\/?$/;

/** Discover and its insight page (`/discover`, `/discover/insight`), not `/discovery`. */
const DISCOVER_PREFIX = /^\/discover(?:\/|$)/;

/** The Data stories list (story detail lives under `/story/`). */
const STORIES_PATH = /^\/stories\/?$/;

// Home and Data stories: flush under the top bar with no outer gutter (VA-3, X-2), and the page
// name beside the logo (F00 #2). The pages own their own background and padding.
registerLayout({
  match: (pathname) => HOME_PATH.test(pathname),
  config: {
    chrome: {
      headerTitle: "Home",
      primaryNavigation: "drawer",
      mainContent: "fullBleed",
    },
  },
});

registerLayout({
  match: (pathname) => STORIES_PATH.test(pathname),
  config: {
    chrome: {
      headerTitle: "Data stories",
      primaryNavigation: "drawer",
      mainContent: "fullBleed",
    },
  },
});

registerLayout({
  match: (pathname) => DISCOVER_PREFIX.test(pathname),
  config: {
    chrome: {
      headerTitle: "Discover",
      primaryNavigation: "drawer",
      mainContent: "fullBleed",
    },
  },
});

registerLayout({
  match: (pathname) => AGENTS_PREFIX.test(pathname),
  config: {
    chrome: {
      headerTitle: "Agents",
      primaryNavigation: "drawer",
      mainContent: "fullBleed",
    },
  },
});

// Connector dataset table page — breadcrumb only, no instance sidebar.
registerLayout({
  match: (pathname) => CONNECTOR_DATASET_TABLE_PATH.test(pathname),
  config: {
    Breadcrumb: ConnectorPageBreadcrumb,
  },
});

// Connector-scoped Data Studio — same breadcrumb-free Data Studio shell as the
// workspace-level route. Registered ahead of the generic connector-detail entry
// below, which would otherwise match this path first (LAYOUT_REGISTRY resolves
// to the first match).
registerLayout({
  match: (pathname) => CONNECTOR_DATA_STUDIO_PATH.test(pathname),
  config: {
    Secondary: DataStudioSecondaryPanelConnected,
    secondaryWidth: "clamp(16.25rem, 21vw, 20rem)",
    chrome: {
      headerTitle: "Data studio",
      primaryNavigation: "compact",
      mainContent: "padded",
    },
  },
});

// Connector detail page — breadcrumb + instance sidebar.
// ConnectorInstanceSidebar uses `width: 100%`, so the column is sized by 20%.
registerLayout({
  match: (pathname) =>
    CONNECTOR_DETAIL_PREFIX.test(pathname) && pathname !== "/data-connectors",
  config: {
    Breadcrumb: ConnectorPageBreadcrumb,
    Secondary: ConnectorInstanceSidebar,
    secondaryWidth: "20%",
  },
});

// Knowledge Graph — secondary sidebar with section navigation (URL-driven).
// KgSecondarySidebar has an intrinsic 220px width, so the column hugs content.
registerLayout({
  match: (pathname) => pathname.startsWith("/knowledge-graph"),
  config: {
    Secondary: KgSecondarySidebarConnected,
  },
});

// Data Studio — compact primary rail + ingested-table column + flexible main.
registerLayout({
  match: (pathname) => DATA_STUDIO_PREFIX.test(pathname),
  config: {
    Secondary: DataStudioSecondaryPanelConnected,
    // Up to 18.75rem visible panel (Figma) + the Shell slot's 1.25rem gutter.
    secondaryWidth: "clamp(16.25rem, 21vw, 20rem)",
    chrome: {
      headerTitle: "Data studio",
      primaryNavigation: "compact",
      mainContent: "padded",
    },
  },
});

// Story pages (plan, build, present, distributions): the design's hamburger plus overlay nav
// drawer, with NO permanent rail (Hardik's decision, 25 Oct, visual fix round VB-1; it replaces
// the earlier deliberate compact rail). Full-bleed so the page owns its own frame (VA-3).
registerLayout({
  match: (pathname) => STORY_DETAIL_PATH.test(pathname),
  config: { chrome: { primaryNavigation: "drawer", mainContent: "fullBleed" } },
});

// Present and Explore story modes: the same hamburger plus overlay drawer, no permanent rail
// (VB-1, coordinator addendum from review D). `/explore` becomes a story path in another unit;
// this entry already covers it. mainContent stays legacy so each page keeps its own chrome.
registerLayout({
  match: (pathname) => /^\/(?:present|explore)(?:\/|$)/.test(pathname),
  config: { chrome: { primaryNavigation: "drawer" } },
});
